import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import styled, { css } from "styled-components";

import { currencyFormatter, findActiveProduct, handleResponse } from "_helpers";
import { checkIfNumberOfPaymentAttemptsExceeded, increaseBillingCounter, resetBillingCounter } from "_helpers/billing";
import { ordersService } from "_services";
import { customerActions, ordersActions } from "_store/actions";
import { REGISTER_TRADEMARK_CODE_ARRAY, UPSELL_PRODUCT_CODES } from "_constants";

import { SpinnerBlock } from "widget/Spinner";

const Text = styled.div`
  font-family: "Montserrat", sans-serif;

  &:not(:first-child) {
    margin-top: 24px;
  }

  ${({ textCenter }) =>
    textCenter &&
    css`
      width: 100%;
      text-align: center;
    `}

  ${({ textJustify }) =>
    textJustify &&
    css`
      width: 100%;
      text-align: justify;
    `}

  ${({ spaceBetween }) =>
    spaceBetween &&
    css`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 24px;
      align-items: end;
    `}

    & p.note {
    font-size: 0.875rem;
    color: #626263;

    & b {
      font-weight: 700;
    }
  }
`;

const SummaryContainer = styled.div`
  padding: 10px;
  /* border-top: 1px solid gray; */
  box-shadow: 0 0 10px lightgray;
  border-radius: 4px;
`;

const StyledBody = styled(Modal.Body)`
  max-height: 80dvh;
  overflow-y: auto;
`;

const ListContainer = styled.div`
  margin-bottom: 24px;
  height: 100%;
  max-height: 100%;
`;

const ProductContainer = styled.div`
  display: flex;
  padding: 12px 4px;
  border-bottom: 1px solid #ecedf1;
  cursor: pointer;

  ${({ index }) =>
    index % 2 !== 0 &&
    css`
      background-color: #fafaff;
    `}

  &:hover {
    background-color: rgba(155, 174, 201, 0.1);
  }

  &:active {
    background-color: rgba(155, 174, 201, 0.25);
  }
`;
const DataCell = styled.div`
  flex: 11;
  &:last-child {
    flex: 1;
  }
`;

const ErrorBox = styled.div`
  padding: 8px;
  background-color: #f0f0f0;
  border-radius: 12px;
`;

const EmptyMessage = styled(Text)`
  color: #7b848d;
  font-weight: bold;
  text-align: center;
  font-family: "Montserrat";
`;

const STEPS = {
  init: "step-init",
  select: "step-select",
  start: "step-start",
  loading: "step-loading",
  success: "step-success",
  error: "step-error",
};

const BASIC_PRICE = 299;
const FEE_PRICE = 300;
const PER_CLASS_PRICE = 150;

function RevivalFromAbandonmentUpsell(props) {
  const dispatch = useDispatch();
  const { onCancel } = props;

  const { ordersList, ordersListLoading } = useSelector(({ orders }) => orders);

  const [step, setStep] = useState(STEPS.select);
  const [error, setError] = useState("");
  const [registerDetails, setRegisterDetails] = useState(null);

  const registerProductsList = ordersList.reduce((acc, order) => {
    const registerProduct = order?.products?.find((product) =>
      REGISTER_TRADEMARK_CODE_ARRAY.includes(product?.code?.code)
    );

    if (registerProduct) {
      return [...acc, { ...registerProduct, crmOrderId: order?.crmOrderId }];
    }

    return acc;
  }, []);

  const extensionProduct = findActiveProduct({
    orders: ordersList,
    orderId: registerDetails?.orderId,
    productCodes: [UPSELL_PRODUCT_CODES.extension],
  });

  const classesCodes = registerDetails?.classes?.reduce((acc, item) => {
    const code = item?.goodsCategory?.split("-")[0]?.trim();

    if (!acc.includes(code)) {
      return [...acc, code];
    }

    return acc;
  }, []);

  let classesCount = classesCodes?.length || 0;

  let total = BASIC_PRICE + FEE_PRICE;

  if (!extensionProduct) {
    total += PER_CLASS_PRICE * classesCount;
  } else {
    classesCount = 0;
  }

  function handleCloseModal({ update = false }) {
    setStep(STEPS.init);
    setError("");
    setRegisterDetails(null);
    onCancel();
    update && dispatch(ordersActions.getOrders());
  }

  async function createUpsell() {
    try {
      const isNumberOfPaymentAttemptsExceeded = checkIfNumberOfPaymentAttemptsExceeded();

      if (isNumberOfPaymentAttemptsExceeded) {
        throw new Error("Uh oh - your card on file has been declined. Please update your payment method to proceed");
      }

      const request = await ordersService.chargeRevivalFee(registerDetails?.orderId, classesCount, true);
      const requestStatus = request?.status;

      handleResponse(request, { allowForbidden: true })
        .then((response) => {
          if (requestStatus === 200) {
            if (!response?.success) {
              if (response?.message === "Card Declined") {
                increaseBillingCounter();
                setError("Uh oh - your card on file has been declined. Please update your payment method to proceed.");
              } else {
                setError(response?.message || "Unknown error");
              }

              setStep(STEPS.error);
            } else {
              resetBillingCounter();
              setStep(STEPS.success);
            }
          } else {
            setError(response?.toString() || "Unknown error");
            setStep(STEPS.error);
          }
        })
        .catch((error) => {
          setError(error?.toString() || "Unknown error");
          setStep(STEPS.error);
        });
    } catch (response) {
      setError(response?.toString() || "Unknown error");
      setStep(STEPS.error);
    }
  }

  useEffect(() => {
    dispatch(customerActions.getPaymentMethods());
  }, [dispatch]);

  switch (step) {
    case STEPS.select:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => handleCloseModal({ update: false })}>
          <Modal.Header closeButton>
            <Text textCenter>
              Which Trademark are you looking
              <br />
              to get an Trademark Revival for?
            </Text>
          </Modal.Header>
          <StyledBody>
            <ListContainer>
              {registerProductsList.map((product, idx) => {
                return (
                  <ProductContainer
                    index={idx}
                    key={`regTm-option-${idx}`}
                    title={"#" + product?.crmOrderId}
                    onClick={() => {
                      setRegisterDetails(product);
                      setStep(STEPS.start);
                    }}
                  >
                    <DataCell>
                      <Text>
                        {product?.markDesc}
                        &nbsp;
                        <span style={{ color: "gray" }}>({product?.trademarkType})</span>
                      </Text>
                    </DataCell>
                    <DataCell>
                      <Text>
                        <span style={{ color: "#c0c0c0" }}>
                          {"#"}
                          {idx + 1}
                        </span>
                      </Text>
                    </DataCell>
                  </ProductContainer>
                );
              })}
              {ordersListLoading && <SpinnerBlock />}
              {!ordersListLoading && registerProductsList?.length < 1 && (
                <EmptyMessage>No relevant trademarks were found.</EmptyMessage>
              )}
            </ListContainer>
          </StyledBody>
        </Modal>
      );

    case STEPS.start:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => handleCloseModal({ update: false })}>
          <Modal.Header closeButton>
            <Text>Trademark Revival</Text>
          </Modal.Header>
          <Modal.Body>
            <Text>
              <p>Would you like to purchase a Trademark Revival?</p>
              <p className="note">
                <b>Important Notice:</b>
                <br />
                If more than <b>2 months</b> have passed since the USPTO issued your <i>Notice of Abandonment</i>, your
                trademark is no longer eligible for revival and your payment will be refunded.
              </p>
              <p className="note">
                Please allow up to <b>5 business days</b> for processing. If your deadline is fewer than 5 business days
                away at the time of purchase, we cannot guarantee timely filing and are{" "}
                <b>not responsible for missed deadlines</b>. In such cases, a refund will be issued.
              </p>
            </Text>
            <div style={{ marginTop: "24px" }}>
              <SummaryContainer>
                <Text spaceBetween>
                  Revival Filing Service: <b>{currencyFormatter(BASIC_PRICE)}</b>
                </Text>
                <Text spaceBetween>
                  USPTO Revival Fee: <b>{currencyFormatter(FEE_PRICE)}</b>
                </Text>
                {!extensionProduct && (
                  <Text spaceBetween>
                    USPTO Extension Fee:{" "}
                    <b>
                      {currencyFormatter(PER_CLASS_PRICE)}&nbsp;X&nbsp;{classesCount}
                    </b>
                  </Text>
                )}
                <hr />
                <Text spaceBetween>
                  Total: <b>{currencyFormatter(total)}</b>
                </Text>
              </SummaryContainer>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setRegisterDetails(null);
                setStep(STEPS.select);
              }}
            >
              Back
            </Button>
            <Button
              type="button"
              variant="success"
              disabled={total <= 0 || classesCount?.length < 1}
              onClick={() => {
                setStep(STEPS.loading);
                createUpsell();
              }}
            >
              Charge
            </Button>
          </Modal.Footer>
        </Modal>
      );

    case STEPS.success:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => handleCloseModal({ update: true })}>
          <Modal.Header closeButton>
            <Text>Trademark Revival</Text>
          </Modal.Header>
          <Modal.Body>
            <Text>Thank you! Trademark Revival request has been placed.</Text>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="success" onClick={() => handleCloseModal({ update: true })}>
              OK
            </Button>
          </Modal.Footer>
        </Modal>
      );

    case STEPS.error:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => handleCloseModal({ update: true })}>
          <Modal.Header closeButton>
            <Text>Trademark Revival</Text>
          </Modal.Header>
          <Modal.Body>
            <ErrorBox>
              <div style={{ color: "red" }}>
                <b>Error:</b>
              </div>
              <div>{error?.replaceAll("Error:", "")}</div>
            </ErrorBox>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="success" onClick={() => handleCloseModal({ update: true })}>
              OK
            </Button>
          </Modal.Footer>
        </Modal>
      );

    case STEPS.loading:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => {}}>
          <Modal.Header>
            <Text>Trademark Revival</Text>
          </Modal.Header>
          <Modal.Body>
            <SpinnerBlock />
          </Modal.Body>
        </Modal>
      );

    default:
      return null;
  }
}

RevivalFromAbandonmentUpsell.propTypes = {
  upsellInfo: PropTypes.object,
  onCancel: PropTypes.func,
};

export default RevivalFromAbandonmentUpsell;
